import { BundleData, ImageSource } from './assets'
import { Layer, Renderer, RenderItem } from './renderer'
import { ImageRect, lineSubImage, subImage } from './vertexWriter'

const lineColors: Record<string, string> = {
  'art/2dart/passivetree/passiveskillscreencurvesnormaltogether': '4a4429',
  'art/2dart/passivetree/passiveskillscreencurvesactivetogether': 'fce174',
  'art/2dart/passivetree/passiveskillscreencurvesintermediatetogether': 'b5713b',
  'art/2dart/passivetree/ascendancypassiveskillscreencurvesnormaltogether': '4a4429',
  'art/2dart/passivetree/ascendancypassiveskillscreencurvesactivetogether': 'fce174',
  'art/2dart/passivetree/ascendancypassiveskillscreencurvesintermediatetogether': 'b5713b',
  'art/2dart/passivetree/atlaspassiveskillscreencurvesnormalbluetogether': '4a4429',
  'art/2dart/passivetree/atlaspassiveskillscreencurvesactivebluetogether': 'fce174',
  'art/2dart/passivetree/atlaspassiveskillscreencurvesintermediatebluetogether': 'b5713b'
}

function strokeColor(image: string, color?: RenderItem['color']) {
  const base = lineColors[image] ?? 'ffffff'
  if (!color) return '#' + base
  let [r, g, b] = [base.substring(0, 2), base.substring(2, 4), base.substring(4, 6)].map((v) => parseInt(v, 16))
  r *= color.r
  g *= color.g
  b *= color.b
  return (
    '#' +
    [r, g, b]
      .map((v) =>
        Math.max(0, Math.min(255, Math.round(v * 255)))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  )
}

export class CanvasRenderer extends Renderer {
  private context: CanvasRenderingContext2D

  constructor(canvas: HTMLCanvasElement, private highQuality: boolean) {
    super(canvas)
    const context = canvas.getContext('2d')
    if (!context) throw Error('Canvas 2D is not supported')
    this.context = context
  }

  private bundleImages: Record<string, ImageSource[]> = {}

  loadBundle(name: string, bundle: BundleData) {
    if (this.loaded.has(name)) return
    super.loadBundle(name, bundle)
    if (!this.bundleImages[name]) {
      this.bundleImages[name] = bundle.map((b) => b.image)
    }
  }

  private items: RenderItem[] = []

  setItems(items: RenderItem[]) {
    super.setItems(items)

    this.items = items.slice().sort((a, b) => a.layer - b.layer)
  }

  private patterns = new Map<ImageSource, CanvasPattern | string>()
  private pattern(image: ImageSource, repeat?: string) {
    let p: CanvasPattern | string | undefined = this.patterns.get(image)
    if (!p) {
      try {
        p = this.context.createPattern(image, repeat ?? null) ?? '#000'
      } catch (e) {
        p = '#000'
      }
      this.patterns.set(image, p)
    }
    return p
  }

  private colorCache = new Map<ImageSource, Record<string, ImageSource>>()
  private colored(image: ImageSource, color: NonNullable<RenderItem['color']>) {
    let cache: Record<string, ImageSource> = this.colorCache.get(image)!
    if (!cache) {
      cache = {}
      this.colorCache.set(image, cache)
    }
    const key = JSON.stringify(color)
    if (!cache[key]) {
      const canvas = new OffscreenCanvas(image.width, image.height)
      console.time('color ' + key)
      const ctx = canvas.getContext('2d')!
      if (color.desaturate) {
        ctx.filter = `grayscale(${(color.desaturate * 100).toFixed(2)}%)`
      }
      ctx.drawImage(image, 0, 0)
      ctx.filter = ''
      ctx.globalCompositeOperation = 'multiply'
      ctx.fillStyle = `rgb(${[color.r, color.g, color.b].map((c) => (c * 255).toFixed(0)).join(',')})`
      ctx.fillRect(0, 0, image.width, image.height)
      ctx.globalAlpha = color.a
      ctx.globalCompositeOperation = 'destination-atop'
      ctx.drawImage(image, 0, 0)
      console.timeEnd('color ' + key)
      cache[key] = canvas
    }
    return cache[key]
  }

  render(transparent?: boolean) {
    const ctx = this.context

    const { size, transform } = this.state

    ctx.imageSmoothingQuality = 'medium'
    ctx.globalCompositeOperation = 'source-over'
    ctx.globalAlpha = 1

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    if (transparent) {
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    } else {
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    }
    ctx.setTransform(transform.scale, 0, 0, transform.scale, transform.x, transform.y)

    for (const item of this.items) {
      const imageRect = this.images[item.image]
      if (!imageRect) continue
      let image = this.bundleImages[imageRect.bundle]?.[imageRect.index]
      if (!image) continue
      let rect: ImageRect = imageRect

      ctx.globalAlpha = 1
      if (item.color) {
        if (this.highQuality) {
          image = this.colored(image, item.color)
        } else {
          ctx.globalAlpha = item.color.a
        }
      }

      if (item.layer === Layer.JEWEL_RADIUS1 || item.layer === Layer.JEWEL_RADIUS2) {
        ctx.globalCompositeOperation = 'lighter'
      } else {
        ctx.globalCompositeOperation = 'source-over'
      }

      // here we draw
      if (item.type === 'background') {
        ctx.fillStyle = this.pattern(image, 'repeat')
        ctx.fillRect(
          -transform.x / transform.scale,
          -transform.y / transform.scale,
          size.width / transform.scale,
          size.height / transform.scale
        )
      } else if (item.type === 'image') {
        const scale = rect.scale * (item.scale ?? 1)
        ctx.save()
        ctx.translate(item.x, item.y)
        if (item.angle) ctx.rotate(item.angle)
        const width = item.size ?? rect.w * scale
        const height = item.size ?? rect.h * scale
        const delta = item.centered ? -0.5 : 0
        ctx.drawImage(image, rect.x, rect.y, rect.w, rect.h, delta * width, delta * height, width, height)
        ctx.restore()
      } else if (item.type === 'twohalf') {
        const width = rect.w * rect.scale
        const height = rect.h * rect.scale
        ctx.save()
        ctx.translate(item.x, item.y)
        ctx.drawImage(image, rect.x, rect.y, rect.w, rect.h, -width / 2, -height, width, height)
        ctx.scale(1, -1)
        ctx.drawImage(image, rect.x, rect.y, rect.w, rect.h, -width / 2, -height, width, height)
        ctx.restore()
      } else if (item.type === 'line') {
        if (this.highQuality) {
          rect = subImage(rect, lineSubImage)
          const dx = item.x2 - item.x1
          const dy = item.y2 - item.y1
          const dist = Math.sqrt(dx * dx + dy * dy)
          ctx.save()
          ctx.transform(dx / dist, dy / dist, -dy / dist, dx / dist, item.x1, item.y1)
          const imWidth = rect.w * rect.scale
          const imHeight = rect.h * rect.scale
          const uScale = dist / imWidth
          for (let start = 0; start < uScale; start += 1) {
            const width = Math.min(1, uScale - start)
            ctx.drawImage(
              image,
              rect.x,
              rect.y,
              rect.w * width,
              rect.h,
              start * imWidth,
              -0.5 * imHeight,
              width * imWidth,
              imHeight
            )
          }
          ctx.restore()
        } else {
          ctx.strokeStyle = strokeColor(item.image, item.color)
          ctx.lineWidth = 11

          ctx.beginPath()
          ctx.moveTo(item.x1, item.y1)
          ctx.lineTo(item.x2, item.y2)
          ctx.stroke()
        }
      } else if (item.type === 'arc') {
        if (this.highQuality) {
          let { a1, a2 } = item
          let diff = a2 - a1
          while (diff <= -Math.PI) diff += Math.PI * 2
          while (diff > Math.PI) diff -= Math.PI * 2
          if (diff < 0) {
            const t = a1
            a1 = a2
            a2 = t
            diff = -diff
          }
          ctx.save()
          ctx.translate(item.x, item.y)
          ctx.beginPath()
          ctx.arc(0, 0, item.r + 50, a1, a2, false)
          ctx.arc(0, 0, item.r - 50, a2, a1, true)
          ctx.closePath()
          ctx.clip()
          const w = rect.w * rect.scale
          const h = rect.h * rect.scale
          ctx.rotate(Math.PI + a1)
          while (diff > 0) {
            ctx.drawImage(image, rect.x, rect.y, rect.w, rect.h, -w, -h, w, h)
            ctx.rotate(Math.PI / 2)
            diff -= Math.PI / 2
          }
          ctx.restore()
        } else {
          ctx.strokeStyle = strokeColor(item.image, item.color)
          ctx.lineWidth = 11

          let diff = item.a2 - item.a1
          while (diff <= -Math.PI) diff += Math.PI * 2
          while (diff > Math.PI) diff -= Math.PI * 2

          ctx.beginPath()
          ctx.arc(item.x, item.y, item.r, item.a1, item.a2, diff < 0)
          ctx.stroke()
        }
      }
    }
  }
}
